import { RootState } from '@AppState/redux-store'
import { Button } from '@Components/common/button'
import { BUTTON_TYPE } from '@Components/common/types'
import { DrawerState, ExpandableDrawer } from '@Components/expandable-drawer'
import * as Dialog from '@radix-ui/react-dialog'
import { Utils } from '@Util/utils'
import { ChangeEvent, useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { dynamicTemplate } from '@Util/dynamic-template.ts'

export type JumpToDrawerProps = {
  currentDrawNumber: number
  latestDraw: number
  open: boolean
  onClose: () => void
  onSubmit: (drawNumber: number) => void
}

const CLASS_NAME = 'gby-k2g-jump-to-draw-drawer'

export const JumpToDrawDrawer = ({
  currentDrawNumber,
  latestDraw,
  open,
  onClose,
  onSubmit,
}: JumpToDrawerProps) => {
  const hostConfig = useSelector((state: RootState) => state.hostConfig)
  const gameConfig = useSelector((state: RootState) => state.gameConfig.gameRules)
  const [drawerOpen, onDrawerOpen] = useState(open)
  const [drawNumber, onDrawerNumberChange] = useState<number | null>(currentDrawNumber)
  const ref = useRef<HTMLInputElement>(null)

  const minDraw = gameConfig?.at(0)?.activeDrawNumberRange.min || 0

  const isOutsideDrawBounds = useCallback(
    (drawingNumber: number) => {
      return drawingNumber < minDraw || drawingNumber > latestDraw + 1
    },
    [minDraw, latestDraw],
  )

  const keyDownInputHandler = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && drawNumber && !isOutsideDrawBounds(drawNumber)) {
        onSubmit(drawNumber)
      }
    },
    [drawNumber, onSubmit, isOutsideDrawBounds],
  )

  const onDrawerNumberInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      onDrawerNumberChange(null)
      return
    }

    // Who knew that Number('') came out to 0? That's why we have the previous check so we can backspace the whole input properly
    const inputValue = Number(e.target.value)
    if (inputValue <= latestDraw + 1) {
      onDrawerNumberChange(inputValue)
    }
  }

  const onDrawerOpenChange = (isOpen: boolean) => {
    onDrawerOpen(isOpen)
    if (!isOpen) {
      onClose()
    }
  }

  const scrollIntoView = () => {
    setTimeout(() => {
      ref.current?.scrollIntoView()
    }, 300)
  }

  const bodyTemplate = useCallback(() => {
    return dynamicTemplate(hostConfig.config?.content.jumpToDraw.bodyTemplate, {
      minDraw: minDraw,
    })
  }, [hostConfig.config?.content.jumpToDraw.bodyTemplate, minDraw])

  return (
    <Dialog.Root open={drawerOpen} onOpenChange={onDrawerOpenChange}>
      <ExpandableDrawer
        classPrefix={CLASS_NAME}
        drawerName={hostConfig.config?.content.jumpToDraw.title}
        hasCloseButton={false}
        state={open ? DrawerState.OPEN : DrawerState.CLOSED}
        scrollable={false}
        onOpenChange={onDrawerOpenChange}
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          ref.current?.focus()
          scrollIntoView()
        }}>
        <div className={`${CLASS_NAME}__container`}>
          <div dangerouslySetInnerHTML={{ __html: bodyTemplate() }}></div>
          <input
            ref={ref}
            className={`${CLASS_NAME}__text-input`}
            type="number"
            inputMode="numeric"
            pattern="\d*"
            value={drawNumber ?? ''}
            onChange={onDrawerNumberInputChange}
            onFocus={scrollIntoView}
            onKeyDown={keyDownInputHandler}
            step={1}
          />
          <div className={`${CLASS_NAME}__container__footer`}>
            <Button text="Cancel" onClick={onClose} />
            <Button
              text={hostConfig.config?.content.jumpToDraw.buttonLabel ?? 'Jump to Draw'}
              onClick={drawNumber ? () => onSubmit(drawNumber) : Utils.noop}
              disabled={!drawNumber || isOutsideDrawBounds(drawNumber)}
              type={BUTTON_TYPE.SUBMIT}
            />
          </div>
        </div>
      </ExpandableDrawer>
    </Dialog.Root>
  )
}
